import type * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

const DRAG_THRESHOLD_PX = 5;

type DragState = {
  mouseDownX: number;
  mouseDownY: number;
  isDragging: boolean;
};

export type UseCombinedDragAndSortParams = {
  canReorder: boolean;
  canSort: boolean;
  dragHandleProps?: Record<string, unknown>;
  handleSortToggle: () => void;
  isDragging?: boolean;
};

export function useCombinedDragAndSort({
  canReorder,
  canSort,
  dragHandleProps,
  handleSortToggle,
  isDragging = false,
}: UseCombinedDragAndSortParams) {
  const [isDragActive, setIsDragActive] = useState(false);
  const dragStateRef = useRef<DragState | null>(null);
  const mouseMoveHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const mouseUpHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const touchMoveHandlerRef = useRef<((e: TouchEvent) => void) | null>(null);
  const touchEndHandlerRef = useRef<((e: TouchEvent) => void) | null>(null);
  const pointerMoveHandlerRef = useRef<((e: PointerEvent) => void) | null>(null);
  const pointerUpHandlerRef = useRef<((e: PointerEvent) => void) | null>(null);

  useEffect(() => {
    return () => {
      if (mouseMoveHandlerRef.current) {
        document.removeEventListener('mousemove', mouseMoveHandlerRef.current);
      }
      if (mouseUpHandlerRef.current) {
        document.removeEventListener('mouseup', mouseUpHandlerRef.current);
      }
      if (touchMoveHandlerRef.current) {
        document.removeEventListener('touchmove', touchMoveHandlerRef.current);
      }
      if (touchEndHandlerRef.current) {
        document.removeEventListener('touchend', touchEndHandlerRef.current);
      }
      if (pointerMoveHandlerRef.current) {
        document.removeEventListener('pointermove', pointerMoveHandlerRef.current);
      }
      if (pointerUpHandlerRef.current) {
        document.removeEventListener('pointerup', pointerUpHandlerRef.current);
      }
    };
  }, []);

  const combinedDragAndSortProps = useMemo(() => {
    // Header drag is pointer-only (keyboard reorder lives in the Columns dialog), so dnd-kit's keyboard cues go.
    const {
      onMouseDown: dragOnMouseDown,
      onTouchStart: dragOnTouchStart,
      'aria-describedby': _dragInstructions,
      'aria-roledescription': _dragRoleDescription,
      ...otherDragProps
    } = (dragHandleProps || {}) as Record<string, unknown> & {
      onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void;
      onTouchStart?: (e: React.TouchEvent<HTMLElement>) => void;
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
      if (!canReorder) return;

      setIsDragActive(false);
      dragStateRef.current = {
        mouseDownX: e.clientX,
        mouseDownY: e.clientY,
        isDragging: false,
      };

      const handleDocumentMouseMove = (e: MouseEvent) => {
        if (dragStateRef.current && !dragStateRef.current.isDragging) {
          const deltaX = Math.abs(e.clientX - dragStateRef.current.mouseDownX);
          const deltaY = Math.abs(e.clientY - dragStateRef.current.mouseDownY);
          if (deltaX > DRAG_THRESHOLD_PX || deltaY > DRAG_THRESHOLD_PX) {
            dragStateRef.current.isDragging = true;
            setIsDragActive(true);
          }
        }
      };

      const handleDocumentMouseUp = () => {
        if (dragStateRef.current && !dragStateRef.current.isDragging && canSort) {
          handleSortToggle();
        }
        dragStateRef.current = null;
        setIsDragActive(false);
        if (mouseMoveHandlerRef.current) {
          document.removeEventListener('mousemove', mouseMoveHandlerRef.current);
          mouseMoveHandlerRef.current = null;
        }
        if (mouseUpHandlerRef.current) {
          document.removeEventListener('mouseup', mouseUpHandlerRef.current);
          mouseUpHandlerRef.current = null;
        }
      };

      mouseMoveHandlerRef.current = handleDocumentMouseMove;
      mouseUpHandlerRef.current = handleDocumentMouseUp;
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp);

      if (dragOnMouseDown) {
        dragOnMouseDown(e);
      }
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLElement>) => {
      if (!canReorder) return;

      setIsDragActive(false);
      if (e.touches.length > 0) {
        dragStateRef.current = {
          mouseDownX: e.touches[0].clientX,
          mouseDownY: e.touches[0].clientY,
          isDragging: false,
        };
      }

      const handleDocumentTouchMove = (e: TouchEvent) => {
        if (dragStateRef.current && !dragStateRef.current.isDragging && e.touches.length > 0) {
          const deltaX = Math.abs(e.touches[0].clientX - dragStateRef.current.mouseDownX);
          const deltaY = Math.abs(e.touches[0].clientY - dragStateRef.current.mouseDownY);
          if (deltaX > DRAG_THRESHOLD_PX || deltaY > DRAG_THRESHOLD_PX) {
            dragStateRef.current.isDragging = true;
            setIsDragActive(true);
          }
        }
      };

      const handleDocumentTouchEnd = () => {
        if (dragStateRef.current && !dragStateRef.current.isDragging && canSort) {
          handleSortToggle();
        }
        dragStateRef.current = null;
        setIsDragActive(false);
        if (touchMoveHandlerRef.current) {
          document.removeEventListener('touchmove', touchMoveHandlerRef.current);
          touchMoveHandlerRef.current = null;
        }
        if (touchEndHandlerRef.current) {
          document.removeEventListener('touchend', touchEndHandlerRef.current);
          touchEndHandlerRef.current = null;
        }
      };

      touchMoveHandlerRef.current = handleDocumentTouchMove;
      touchEndHandlerRef.current = handleDocumentTouchEnd;
      document.addEventListener('touchmove', handleDocumentTouchMove);
      document.addEventListener('touchend', handleDocumentTouchEnd);

      if (dragOnTouchStart) {
        dragOnTouchStart(e);
      }
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLElement>) => {
      if (!canReorder) return;

      setIsDragActive(false);
      dragStateRef.current = {
        mouseDownX: e.clientX,
        mouseDownY: e.clientY,
        isDragging: false,
      };

      const handleDocumentPointerMove = (e: PointerEvent) => {
        if (dragStateRef.current && !dragStateRef.current.isDragging) {
          const deltaX = Math.abs(e.clientX - dragStateRef.current.mouseDownX);
          const deltaY = Math.abs(e.clientY - dragStateRef.current.mouseDownY);
          if (deltaX > DRAG_THRESHOLD_PX || deltaY > DRAG_THRESHOLD_PX) {
            dragStateRef.current.isDragging = true;
            setIsDragActive(true);
          }
        }
      };

      const handleDocumentPointerUp = () => {
        if (dragStateRef.current && !dragStateRef.current.isDragging && canSort) {
          handleSortToggle();
        }
        dragStateRef.current = null;
        setIsDragActive(false);
        if (pointerMoveHandlerRef.current) {
          document.removeEventListener('pointermove', pointerMoveHandlerRef.current);
          pointerMoveHandlerRef.current = null;
        }
        if (pointerUpHandlerRef.current) {
          document.removeEventListener('pointerup', pointerUpHandlerRef.current);
          pointerUpHandlerRef.current = null;
        }
      };

      pointerMoveHandlerRef.current = handleDocumentPointerMove;
      pointerUpHandlerRef.current = handleDocumentPointerUp;
      document.addEventListener('pointermove', handleDocumentPointerMove);
      document.addEventListener('pointerup', handleDocumentPointerUp);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
      if (isDragActive || isDragging || !canSort) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSortToggle();
      }
    };

    return {
      ...otherDragProps,
      onMouseDown: canReorder ? handleMouseDown : undefined,
      onTouchStart: handleTouchStart,
      onPointerDown: canReorder ? handlePointerDown : undefined,
      // Without reorder the sort target is a native button, which already fires click on Enter and Space.
      onKeyDown: canSort && canReorder ? handleKeyDown : undefined,
      ...(canSort && !canReorder ? { onClick: handleSortToggle } : {}),
    };
  }, [canReorder, canSort, dragHandleProps, handleSortToggle, isDragActive, isDragging]);

  return { combinedDragAndSortProps, isDragActive };
}
